// The helper's own screen, as pure components (bead hf-e6-commitments-coverage-udt.9).
//
// Copy from `@herefor/product-language`, components from `@herefor/design-system`, and
// no query, no mutation and no permission decision anywhere in the file. Everything
// here is testable without a server, which is the point: this is the screen the
// product's adoption rests on, and it must be possible to read its words in a test.
//
// ── WHAT THIS SCREEN REFUSES TO DO ─────────────────────────────────────────────
//
// No badge, no count of "outstanding" anything, no streak, no red. A section with two
// asks in it looks exactly like a section with none, apart from having two asks in it.
// The product's tone rules forbid guilt mechanics, and a helper's own screen is where
// they would be easiest to add and most damaging: this is somebody's week, not a
// queue they are behind on.
//
// The one place emphasis IS warranted is a person's own agreement changing shape —
// and that already has a state chip with reviewed words ("Please recheck · Something
// important changed"). Reusing it, rather than inventing a second signal here, is
// what keeps one fact from having two voices.
//
// ── AND WHY THE ASK AND RECHECK LISTS ARE NOT IN THIS FILE ─────────────────────
//
// They are landed surfaces with their own tests, their own refusal vocabulary and
// their own answer-in-flight state (`PendingAskList`, `RecheckList`). This screen
// PLACES them; it does not re-render them. A second copy of the ask card living here
// would be a second set of words for one question, and the two would drift the first
// time either bead moved.

import { Card, EmptyState, StateChip } from "@herefor/design-system";
import {
  AVAILABILITY_EXPIRY_MESSAGES as EXPIRY_COPY,
  AVAILABILITY_MESSAGES,
  MY_WEEK_MESSAGES as COPY,
  USER_VISIBLE_STATES,
} from "@herefor/product-language";
import type { ReactNode } from "react";

import type { MyAvailabilitySummaryView, MyCommitmentRowView } from "./myWeekData";

/** Which section a deep link asked for, so it can be brought to the front. */
export type MyWeekFocus = "asks" | "rechecks" | null;

export interface MyWeekLayoutProps {
  /** The asks list, already wired to its own query and mutation. */
  readonly asks: ReactNode;
  /** The rechecks list, likewise. */
  readonly rechecks: ReactNode;
  readonly commitments: ReactNode;
  readonly availability: ReactNode;
  /** The section a notification deep link named, brought to the top (Epic 7). */
  readonly focus?: MyWeekFocus;
}

/**
 * The screen: four sections, in the order a helper needs them.
 *
 * ANSWER-FIRST is the whole layout argument. Things that need a decision from this
 * person come before things that are already settled, because somebody who gives the
 * app ninety seconds must spend them on the question rather than on scrolling past
 * their own agreements to find it.
 *
 * A deep link reorders exactly one thing — it lifts the named section above the other
 * — and changes nothing else. It does not filter, highlight or scroll: a notification
 * about an ask that has since lapsed then lands on a list that calmly says there is
 * nothing to answer, instead of on an empty highlight or a dead end.
 */
export function MyWeekLayout({
  asks,
  rechecks,
  commitments,
  availability,
  focus = null,
}: MyWeekLayoutProps) {
  const answerable = focus === "rechecks" ? [rechecks, asks] : [asks, rechecks];

  return (
    <div className="my-week" data-my-week-focus={focus ?? "none"}>
      <h1 className="my-week__heading">{COPY.heading}</h1>
      <p className="my-week__introduction">{COPY.introduction}</p>

      {/* EVERY SECTION RENDERS, EMPTY OR NOT, and that is the bead's instruction
          rather than an oversight. A helper who opens this screen on a quiet Tuesday
          must be able to see that the app is working and that there is genuinely
          nothing waiting — a screen that hid its empty sections would be
          indistinguishable from one that had failed to load them. Each landed list
          brings its own warm empty state; the two panels below carry theirs. */}
      {answerable.map((section, index) => (
        // Index keys are correct here and only here: the two entries are a fixed pair
        // whose order is the only thing that varies, so a key derived from position
        // is exactly the identity React needs to keep both mounted.
        <div className="my-week__section" key={index}>
          {section}
        </div>
      ))}
      <div className="my-week__section">{commitments}</div>
      <div className="my-week__section">{availability}</div>
    </div>
  );
}

export interface MyCommitmentsPanelProps {
  readonly commitments: readonly MyCommitmentRowView[];
}

/**
 * What this person has agreed to, with the SUPPORT window as the headline (NED-002).
 *
 * The window comes first and largest; the appointment, if there is one, is a quieter
 * line prefixed so it can only read as context. That ordering is the bead's one
 * non-negotiable piece of layout, and it exists because the two times are routinely
 * different and people routinely confuse them.
 *
 * No withdrawal control. Stepping back from an agreement is a real decision with real
 * consequences for somebody else's week, and it belongs on the need — with the other
 * people helping, the coverage count and the words that explain what happens next —
 * not one tap from a list somebody skims over breakfast.
 */
export function MyCommitmentsPanel({ commitments }: MyCommitmentsPanelProps) {
  return (
    <section aria-labelledby="my-week-commitments-heading" className="my-week-commitments">
      <h2 id="my-week-commitments-heading">{COPY.commitmentsHeading}</h2>

      {commitments.length === 0 ? (
        <EmptyState description={COPY.commitmentsEmpty} title={COPY.commitmentsEmptyTitle} />
      ) : (
        <ul className="my-week-commitments__list">
          {commitments.map((commitment) => (
            <li key={commitment.commitmentId}>
              <Card title={commitment.label} titleLevel={3}>
                {/* The hours they agreed to, first and alone on the line. */}
                <p className="my-week-commitments__when">
                  {commitment.dateLabel} · {COPY.neededFrom(commitment.windowLabel)}
                </p>

                {commitment.eventTitle === null ? null : (
                  <p className="my-week-commitments__event">
                    {COPY.aroundEvent(commitment.eventTitle)}
                  </p>
                )}

                {commitment.alsoHelping.length === 0 ? null : (
                  <p className="my-week-commitments__with">
                    {COPY.alsoHelping(commitment.alsoHelping.join(", "))}
                  </p>
                )}

                {/* The need's own state, in the product's reviewed words. Projected
                    from the server's derivation, never recomputed (COV-002). */}
                <p className="my-week-commitments__state">
                  <StateChip
                    detail={USER_VISIBLE_STATES[commitment.stateKey].explanation}
                    label={USER_VISIBLE_STATES[commitment.stateKey].label}
                    state={commitment.stateKey}
                  />
                </p>

                <p className="my-week-commitments__link">
                  <a href={commitment.href}>{commitment.label}</a>
                </p>
              </Card>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export interface MyAvailabilityPanelProps {
  readonly summary: MyAvailabilitySummaryView | null;
  /** `/c/<ref>/availability` — where times are actually added and changed. */
  readonly availabilityHref: string;
}

/**
 * The times this person listed, and — only when it is true — that they are ending.
 *
 * ── THE NUDGE IS A FACT, NOT A PROD ────────────────────────────────────────────
 *
 * The Epic 5 renewal words are reused verbatim ("Your listed times end on …",
 * "Would you like to add more times you could be asked about? Nothing is added
 * automatically"). They are shown only inside the circle's own renewal horizon, and
 * they say what is true and stop. Availability lapsing is not a failure — it is the
 * design working, because an unrenewed offer must not keep answering for somebody who
 * has stopped choosing to make it (AVL-019).
 *
 * ── AND IT LINKS RATHER THAN EDITS ─────────────────────────────────────────────
 *
 * Adding times is a real editor with weeks, blocks, help modes and DST rules, and it
 * already exists one route away. Reproducing any part of it here would be a second
 * place to change one thing; the door is the honest affordance.
 */
export function MyAvailabilityPanel({ summary, availabilityHref }: MyAvailabilityPanelProps) {
  return (
    <section aria-labelledby="my-week-availability-heading" className="my-week-availability">
      <h2 id="my-week-availability-heading">{COPY.availabilityHeading}</h2>

      {summary === null ? (
        <EmptyState description={COPY.availabilityEmpty} title={COPY.availabilityEmptyTitle} />
      ) : (
        <>
          <p className="my-week-availability__summary">
            {COPY.availabilityWindowCount(summary.windowCount)} ·{" "}
            {COPY.availabilityUntil(summary.untilDateLabel)}
          </p>

          {/* The promise availability makes, and the one it does not. Said here as
              well as on the availability screen because this is where somebody reads
              it in passing (AVL-026). */}
          <p className="my-week-availability__meaning">{AVAILABILITY_MESSAGES.meaning}</p>

          {summary.endingSoon ? (
            <div className="my-week-availability__renewal">
              <p className="my-week-availability__renewal-heading">
                {summary.expired
                  ? EXPIRY_COPY.endedHeading(summary.untilDateLabel)
                  : EXPIRY_COPY.endingHeading(summary.untilDateLabel)}
              </p>
              <p>{EXPIRY_COPY.promptBody}</p>
            </div>
          ) : null}
        </>
      )}

      <p className="my-week-availability__link">
        <a href={availabilityHref}>
          {summary?.endingSoon === true ? EXPIRY_COPY.addMore : COPY.viewAvailability}
        </a>
      </p>
    </section>
  );
}
